/** ResourceUsage describes site-wide resource limits and usage */
export interface ResourceUsage {
  /**
   * Used storage sum of Sandbox and Live environments, bytes
   * Replaced by live_used_storage_in_bytes
   * @deprecated
   * @replacedBy live_used_storage_in_bytes + sandbox_used_storage_in_bytes
   */
  totalUsedStorageInBytes?: string | null;
  /** Max storage, bytes */
  totalUsedStorageInBytesLimit?: string | null;
  /** Total number of native collections created */
  collectionCount?: number | null;
  /** Max number of native collections allowed */
  collectionCountLimit?: number | null;
  /**
   * Total number of items in native collections sum of Sandbox and Live environments
   * Replaced by live_item_count
   * @deprecated
   * @replacedBy live_item_count + sandbox_item_count
   */
  totalItemCount?: string | null;
  /** Max number of items in native collections */
  totalItemCountLimit?: string | null;
  /** Resource usages per data collection */
  dataCollectionUsages?: DataCollectionResourceUsage[];
  /** Used storage in Live environment */
  liveUsedStorageInBytes?: string | null;
  /** Number of items in native collections in Live environment */
  liveItemCount?: string | null;
  /** Used storage in Sandbox environment */
  sandboxUsedStorageInBytes?: string | null;
  /** Number of items in native collections in Sandbox environment */
  sandboxItemCount?: string | null;
}

export interface DataCollectionResourceUsage {
  /** Data Collection ID */
  dataCollectionId?: string;
  /** Data Collection display name */
  displayName?: string | null;
  /** Data Collection item count in the live environment */
  liveItemCount?: string;
  /** Data Collection item count in the sandbox environment, none if disabled */
  sandboxItemCount?: string | null;
  /** Data Collection used storage in bytes in the live environment */
  liveUsedStorageInBytes?: string;
  /** Data Collection used storage in bytes in the sandbox environment, none if disabled */
  sandboxUsedStorageInBytes?: string | null;
  /** Type of data collection, currently only NATIVE are returned in this API */
  dataCollectionType?: CollectionTypeWithLiterals;
  /** Indicates if the collection is deleted */
  deleted?: boolean;
}

export enum CollectionType {
  /** User-created collection. */
  NATIVE = 'NATIVE',
  /** [Collection](https://support.wix.com/en/article/velo-working-with-wix-app-collections-and-code#what-are-wix-app-collections) created by a Wix app when it is installed. This type of collection can be modified dynamically by that app (for example, Wix Forms). */
  WIX_APP = 'WIX_APP',
  /** Collection created by a Wix Blocks app. */
  BLOCKS_APP = 'BLOCKS_APP',
  /** Collection located in externally connected storage. */
  EXTERNAL = 'EXTERNAL',
}

/** @enumType */
export type CollectionTypeWithLiterals =
  | CollectionType
  | 'NATIVE'
  | 'WIX_APP'
  | 'BLOCKS_APP'
  | 'EXTERNAL';

export interface GetResourceUsageRequest {
  /** ResourceUsage fields to return, if empty all are returned */
  fields?: string[];
  /** If true, operation queries collections for up-to-date values (rather than using cached values) */
  consistentRead?: boolean;
}

export interface GetResourceUsageResponse {
  /** The retrieved ResourceUsage */
  resourceUsage?: ResourceUsage;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface Empty {}
